import { isEmpty } from 'lodash';
import { CSSProperties, useCallback, useContext, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MultiSelectUserRow } from '@shared/components/blocks/lib/inputAssignee';
import { MBImage } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelectModal } from '@shared/components/multiSelect';
import { PersonPill, Pill } from '@shared/components/pill';
import { MBTextBody1 } from '@shared/components/typography';
import { PersonField, usePersons } from '@shared/data/entity/fields/PersonField';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { useWorkspaceDetails } from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { useWorkflowUsers } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { EditFieldContainer } from './EditFieldContainer';

const MAX_PEOPLE_TO_SHOW = 10;
const MAX_PILL_WIDTH = 180;

interface Props {
  field: PersonField | null;
  workflowTemplateId: string;
  component: API.PersonComponentFragment;
  updateField: (componentId: string, field: PersonField | null) => void;
  error?: string;
  allowClear?: boolean;
  style?: CSSProperties;
}

export function PersonEditField({
  field,
  workflowTemplateId,
  component,
  updateField,
  error,
  allowClear,
  style,
}: Props) {
  const workspaceDetails = useWorkspaceDetails();
  const workspaceUsers = useWorkflowUsers(workflowTemplateId);

  const { persons: selectedPersons, loading: personsLoading } = usePersons(field);

  const match = useCallback((user: User, query: string) => {
    const lowercaseQuery = query.toLowerCase();
    return (
      user.username.toLowerCase().includes(lowercaseQuery) ||
      user.fullName.toLowerCase().includes(lowercaseQuery)
    );
  }, []);

  // Combine workspaceUsers with any selectedPersons that aren't already in workspaceUsers
  const availableUsers = useMemo(() => {
    const workspaceUserIds = new Set(workspaceUsers.map(u => u.id));
    const additionalSelectedUsers = selectedPersons.filter(
      person => !workspaceUserIds.has(person.id),
    );
    return [...additionalSelectedUsers, ...workspaceUsers];
  }, [workspaceUsers, selectedPersons]);

  const onChange = useCallback(
    (changedUsers: User[]) => {
      if (isEmpty(changedUsers)) {
        updateField(component.id, null);
      } else {
        updateField(
          component.id,
          new PersonField(
            changedUsers.map(user => user.id),
            changedUsers,
          ),
        );
      }
    },
    [component.id, updateField],
  );

  const onClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const workspaceImageUrl = workspaceDetails.get(workflowTemplateId)?.imageUrl;
  const isReadOnly = component.readonly;
  const hasUsers = selectedPersons.length > 0;
  const showClearButton = hasUsers && !isReadOnly && allowClear;
  const anchorName = getAnchorName('person-edit-field', component.id);
  const printContent = selectedPersons.map(u => u.fullName).join(', ') || undefined;

  if (isReadOnly) {
    return (
      <PersonSummary
        selectedUsers={selectedPersons}
        anchorName={anchorName}
        component={component}
        error={error}
        loading={personsLoading}
        style={style}
        printContent={printContent}
      />
    );
  }

  return (
    <MBMultiSelectModal<User>
      label={component.label ?? ''}
      anchorName={anchorName}
      onChange={onChange}
      selectAll
      renderSelectAll={() => (
        <View flex row centerVertically paddingVertical={1}>
          <MBImage
            style={styles.workflowImage}
            source={{
              uri: workspaceImageUrl ?? '',
            }}
          />
          <View paddingLeft={2} />
          <MBTextBody1>{i18n('form.placeholder.everyone')}</MBTextBody1>
        </View>
      )}
      renderItem={user => <MultiSelectUserRow user={user} />}
      values={availableUsers}
      selectedValues={selectedPersons}
      keyExtractor={user => String(user.id)}
      match={match}
    >
      <PersonSummary
        anchorName={anchorName}
        selectedUsers={selectedPersons}
        component={component}
        showClearButton={showClearButton}
        onClear={onClear}
        error={error}
        loading={personsLoading}
        style={style}
        printContent={printContent}
      />
    </MBMultiSelectModal>
  );
}

function PersonSummary({
  selectedUsers,
  component,
  anchorName,
  showClearButton,
  onClear,
  error,
  loading,
  style,
  printContent,
}: {
  selectedUsers: User[];
  component: API.PersonComponentFragment;
  anchorName: string;
  showClearButton?: boolean;
  onClear?: () => void;
  error?: string;
  loading?: boolean;
  style?: CSSProperties;
  printContent?: string;
}) {
  const colors = useContext(ColorThemeContext);
  const hasUsers = !isEmpty(selectedUsers);
  const color = hasUsers ? colors.foregroundActive : colors.foregroundInactive;
  const peopleToShow: User[] = selectedUsers.slice(0, MAX_PEOPLE_TO_SHOW);
  const remainingPersonCount = selectedUsers.length - MAX_PEOPLE_TO_SHOW;
  const responsiveForms = useResponsiveFormContext();

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showLabel={hasUsers || responsiveForms}
      showClearButton={showClearButton}
      onClear={onClear}
      error={error}
      style={style}
      printContent={printContent}
    >
      {loading && <MBLoadingIndicator style={{ alignItems: 'flex-start' }} />}
      {!loading && isEmpty(selectedUsers) && (
        <View paddingVertical={1.25}>
          <MBTextBody1 style={{ color }}>{component.placeholder}</MBTextBody1>
        </View>
      )}
      {!loading && (
        <MBView style={styles.personPillList}>
          {peopleToShow.map(user => (
            <PersonPill
              key={user.id}
              user={user}
              maxWidth={MAX_PILL_WIDTH}
              showLastName={false}
              pillStyle={styles.personPillStyle}
              textStyle={styles.personPillTextStyle}
            />
          ))}
          {remainingPersonCount > 0 && (
            <Pill
              backgroundColor="white"
              sections={[`+${remainingPersonCount} others`]}
              round
              noBorder
              textStyle={styles.personPillTextStyle}
            />
          )}
        </MBView>
      )}
    </EditFieldContainer>
  );
}

const styles = StyleSheet.create({
  workflowImage: {
    height: grid(5),
    width: grid(5),
    borderRadius: grid(2.5),
  },
  personPillList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingVertical: grid(0),
    // cheat rounded tags left a bit (optical alignment)
    marginLeft: -grid(1),
  },
  personPillStyle: { marginVertical: grid(0.25), paddingVertical: 0 },
  personPillTextStyle: { fontSize: 14 },
});
